import { Avatar, Button, Card } from '@parsina/framework';
import { ChevronLeft } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import StatusChip from '@parsina/framework/clinical/src/components/common/StatusChip';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { ENC_STATUS, cellValue, colLabel } from '../../screens/Encounters/Encounters.shared';

// ─────────────────────────────────────────────────────────────────────────────
// One past visit, as a card (§B.2 ③).
//
// The phone's answer to the desktop's six-column table. It carries the same six
// facts and reads them through the SAME `cellValue`/`colLabel` definition the
// table does, so a file number is formatted identically on both surfaces and a
// column renamed once is renamed everywhere.
//
// The WHOLE card is the target, not a «مشاهدهٔ پرونده» button in its corner:
// there is one thing to do with a past visit — open the record — and a 44px
// button inside a 90px card is a smaller target than the card itself.
// ─────────────────────────────────────────────────────────────────────────────

const Meta = ({ label, value, mono = false }) => (
  <div style={{ background: C.bg, borderRadius: '0.5rem', padding: '0.375rem 0.5rem', minWidth: 0 }}>
    <div style={{ fontSize: '0.625rem', color: C.textMuted, marginBottom: '0.125rem' }}>{label}</div>
    <div
      style={{
        fontSize: '0.75rem', fontWeight: 600, color: C.textPrimary,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
      }}
    >
      {value}
    </div>
  </div>
);

const MobileEncounterCard = ({ encounter, clinicName, onOpen }) => {
  const i18n = useTranslation();
  const { t } = i18n;
  const status = ENC_STATUS[encounter.status] || { key: encounter.status };
  const name = encounter.patientName || t('encounters.unknownPatient');

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <Button
        variant="ghost"
        data-testid={`encounters-card-${encounter.id}`}
        aria-label={t('encounters.recordOf', { name })}
        disabled={!encounter.patientId}
        onClick={() => encounter.patientId && onOpen(encounter)}
        className="h-auto min-h-11 w-full flex-col items-stretch gap-2.5 rounded-none p-3 text-start"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <Avatar name={encounter.patientName || ''} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* `dir="auto"`: a patient's name IS user content, and a Latin name
                under an RTL document otherwise puts its punctuation on the
                wrong side. */}
            <div
              dir="auto"
              style={{
                fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {name}
            </div>
            <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.125rem' }}>
              {cellValue('type', encounter, null, i18n)}
            </div>
          </div>
          <StatusChip
            status={status.key}
            label={status.labelKey ? t(status.labelKey) : undefined}
          />
          <ChevronLeft
            size={16}
            aria-hidden
            style={{ color: C.textMuted, flexShrink: 0, transform: 'scaleX(var(--dir-flip))' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem' }}>
          <Meta label={colLabel(t, 'date')} value={cellValue('date', encounter, null, i18n)} />
          <Meta label={colLabel(t, 'fileNumber')} value={cellValue('fileNumber', encounter, null, i18n)} mono />
          {clinicName && (
            <div style={{ gridColumn: '1 / -1' }}>
              <Meta label={colLabel(t, 'clinic')} value={clinicName} />
            </div>
          )}
        </div>
      </Button>
    </Card>
  );
};

export default MobileEncounterCard;
